import React, { useEffect, useRef, useState } from 'react';
import './quests-landing.styles.scss';
import { useSelector } from 'react-redux';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHand } from '@fortawesome/free-regular-svg-icons';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { getCollectionFromFirestore, getQueryFromFirestore } from '@/utils/getDataFromDbSimplified';
import PlayerQuestItem from './player-quest-item.component';
import { properCase } from '@/utils/properCase';
import PlayerQuestPage from './player-quest-page.component';
import { checkElementAccess } from '@/utils/checkElementAccess';
import { completeGameElementData } from '@/components/PREVIEW/previewGameElements';

const PlayerQuestsLanding = ({
  gameData,
  playerData,
  gameElements,
  gameStats,
  teamMembers,
  myTeams
}) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const [typeFilter, setTypeFilter] = useState('available');
  const [questsToDisplay, setQuestsToDisplay] = useState(null);
  const [ progressFetched, setProgressFetched ] = useState(false);
  const playerProgressRef = useRef({});
  const questListRef = useRef({});
  let questsListener;
  let progressListener;
  const navigate = useNavigate();

  useEffect(() => {
    const pageTitle = document.getElementById('page-title');
    if (pageTitle) {
      pageTitle.innerText = 'Quests';
    }

    return () => {
      if (questsListener) questsListener();
      if (progressListener) progressListener();
    }
  }, []);

  useEffect(() => {
    if (!gameElements) return;
    getQuestsFromDb();
  }, [gameElements, playerProgressRef.current]);

  useEffect(() => {
    if (!Object.values(questListRef.current).length) return;
    filterQuests();
  }, [typeFilter, questListRef.current]);

  function getPlayerProgressFromDb() {
    if (progressFetched) return;
    setProgressFetched(true);
    if (location.pathname.includes('/preview/manager/')) {
      /**
       * GET PLAYER QUEST DATA FROM PREVIEW PLAYER DATA
       */
      getQuestsFromDb();
      return;
    }
    getCollectionFromFirestore({
      'path': `games/${gameData.gameId}/players/${playerData.playerId}/quests`,
      'callback': callback,
      'handleListener': handleListener,
    });
    function callback(data) {
      playerProgressRef.current = {
        ...playerProgressRef.current,
        ...{[data.questId]: data}
      } 
      getQuestsFromDb();
    }
    function handleListener(unsub) {
      progressListener = unsub;
    }
  }

  function getQuestsFromDb() {
    if (location.pathname.includes('/preview/manager/')) {
      const questObj = {};
      Object.values(completeGameElementData)
        .filter(e => e.type === 'quest' && e.status === 'active')
        .forEach(e => {
          questObj[e.id] = e;
        });
      questListRef.current = questObj;
      getPlayerProgressFromDb();
      buildQuestList();
      return;
    }
    // Get quests from DB, then get Player Quests, then build quest list
    getQueryFromFirestore({
      'path': `games/${gameData.gameId}/gameElements`,
      'queryArray': [
        {
          'field': 'type',
          'operator': '==',
          'value': 'quest'
        },
      ],
      'callback': callback,
      'handleListener': handleListener,
    });
    function callback(data) {
      if (data.status !== 'active') return; 
      questListRef.current = {
        ...questListRef.current,
        ...{[data.id]: data}
      };
      getPlayerProgressFromDb();
      buildQuestList();
    }
    function handleListener(unsub) {
      questsListener = unsub;
    }
  }

  function buildQuestList() {
    console.log('building quest list');
    if (!gameElements) return;

    const activeQuests = [...Object.values(questListRef.current)];
    const questsWithStatus = activeQuests.map((quest) => {
      console.log(quest);
      const tempElements = {
        ...gameElements,
        [quest.id]: quest
      };
      const access = checkElementAccess({
        playerData: playerData,
        gameElements: tempElements,
        elementId: quest.id,
        gameStats: gameStats,
        teamMembers: teamMembers,
        type: 'quest'
      });
      console.log(access);
      if (!access) return {};
      if (!access.access) {
        return { ...quest, questStatus: 'locked', missingReqs: access.arr };
      }
      const playerProgressData = playerProgressRef.current[quest.id];
      console.log(playerProgressData);
      let status = 'available';
      if (playerProgressData?.status === 'complete') {
        status = 'complete';
      } else if (playerProgressData?.status === 'inProgress') {
        status = 'inProgress';
      }
      return { ...quest, questStatus: status };
    });
    questListRef.current = questsWithStatus.reduce((acc, quest) => {
      acc[quest.id] = quest;
      return acc;
    }, {});
    filterQuests(Object.values(questListRef.current));
  }

  function filterQuests(list) {
    console.log(list);
    const source = list || Object.values(questListRef.current);
    let filtered;
    if (typeFilter === 'available') {
      filtered = source.filter(q => q.questStatus === 'available' || q.questStatus === 'inProgress');
    } else if (typeFilter === 'closed') {
      filtered = source.filter(q => q.questStatus === 'locked');
    } else {
      filtered = source.filter(q => q.questStatus === typeFilter);
    }
    setQuestsToDisplay([...filtered]);
  }

  return (
    <div className={`player-quests ${screenSize}`}>
      <div className='player-quests-content'>
        <Routes>
          <Route path='' element={
            <div className='g-card'>
              <div className='player-title'>
                <ImageContainer
                  src='/images/icons/quests.png'
                  alt='quests icon'
                  className='player-title-icon'
                />
                Go On a Quest
              </div>
              <hr />
              <div className={`quests-grid ${screenSize}`}>
                <div className='left-col'>
                  <div className='quests-left-menu'>
                    <button
                      className={`g-button med-btn ${typeFilter === 'available' ? 'primary' : ''}`}
                      onClick={() => setTypeFilter('available')}
                    >
                      <FontAwesomeIcon icon={faHand} />
                      {screenSize !== 'mobile' ? 'Available' : ''}
                    </button>
                    <button
                      className={`g-button med-btn ${typeFilter === 'closed' ? 'primary' : ''}`}
                      onClick={() => setTypeFilter('closed')}
                    >
                      <FontAwesomeIcon icon={faLock} />
                      {screenSize !== 'mobile' ? 'Locked' : ''}
                    </button>
                    <button
                      className={`g-button med-btn ${typeFilter === 'complete' ? 'primary' : ''}`}
                      onClick={() => setTypeFilter('complete')}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {screenSize !== 'mobile' ? 'Complete' : ''}
                    </button>
                  </div>
                </div>
                <div className='right-col'>
                  {
                    (screenSize === 'mobile' || screenSize === 'tablet') &&
                    <div className='small-screen-header'>
                      <h2>{properCase(typeFilter)}</h2>
                      <hr />
                    </div>
                  }
                  <div className='player-quest-list-container'>
                    {
                      (questsToDisplay && questsToDisplay.length > 0)
                        ?
                        <div className={`player-quest-list ${screenSize}`}>
                          {
                            questsToDisplay
                              .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
                              .map(quest => (
                                <div key={quest.id}>
                                  <PlayerQuestItem
                                    quest={quest}
                                    onClick={() => navigate(quest.path)}
                                  />
                                </div>
                              ))
                          }
                        </div>
                        :
                        <div>
                          <h4>No {typeFilter} quests...</h4>
                        </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          } />
          <Route path=':questPath/*' element={
            <PlayerQuestPage
              gameData={gameData}
              gameElements={gameElements}
              gameStats={gameStats}
              teamMembers={teamMembers}
              playerData={playerData}
              playerProgress={playerProgressRef.current}
            />
          } />
        </Routes>
        <div className="g-space-2"></div>
      </div>
    </div>
  )
}

export default PlayerQuestsLanding
